import { toast } from 'react-toastify'
import { Link, useNavigate } from 'react-router-dom'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { DuplicateIcon, PencilIcon } from '@heroicons/react/outline'

import { SplitModal } from '../components/Modal'
import { PageHeader } from 'components/PageHeader'
import { Pagination } from 'components/Pagination'
import { LoadingCircle } from 'components/Loading'
import { SortIcon } from 'components/Table/SortIcon'
import { SorUptIcon } from 'components/Table/SortUpIcon'
import { SimpleButton } from 'components/FormInputs/Button'
import { SortDownIcon } from 'components/Table/SortDownIcon'
import { ProtectedSection } from 'components/ProtectedSection'
import { SimpleCustomSelect } from 'components/FormInputs'
import { RowActionsDropdown } from 'components/RowActionsDropdown'

import config from 'config'
import { list } from '../services/read'
import { translations } from '../translations'
import { useURLParams } from 'hooks/useURLParams'
import { Order, OrderStatus, PlanType, Role } from 'models'
import { document, date, amount, currency, time } from 'utils'
import { QueryFilter, ReadResult } from 'interfaces/queryOptions'
import { Label } from 'components/Label'
import { SearchBar } from 'components/Search'
import { getOrderStatusAndDate } from 'utils/order-status'
import { DateRangePickerComponent } from 'components/Date/DateRangePicker'
import { formatDate } from 'utils/datetime'

const statusFilters = [
  { value: '', label: 'Selecione um estado' },
  { value: OrderStatus.pending, label: 'Pendente' },
  { value: OrderStatus.approved, label: 'Aprovado' },
  { value: OrderStatus.in_line, label: 'Na fila' },
  { value: OrderStatus.in_progress, label: 'Em progresso' },
  { value: OrderStatus.updating_fiscal_data, label: 'Emitindo nota fiscal' },
  { value: OrderStatus.ready_for_pick_up, label: 'Pronto para entrega' },
  { value: OrderStatus.shipping, label: 'Em envio' },
  { value: OrderStatus.delivered, label: 'Entregue' },
  { value: OrderStatus.canceled, label: 'Cancelado' },
  { value: OrderStatus.migrated, label: 'Migrado' }
]

const removeFilter = (filters: QueryFilter[], key: string): QueryFilter[] => {
  if (key === 'ordered_at') {
    return filters.filter((filter) => filter.key !== 'ordered_at')
  } else {
    return filters.filter((filter) => filter.key === 'ordered_at')
  }
}

export function List() {
  const navigate = useNavigate()

  const query = useURLParams()
  const queryParams = useMemo(() => {
    const params = {
      filters: [],
      search: ''
    }

    const search = query.get('search')
    const filters = query.get('q')

    if (search) params.search = search
    if (filters) params.filters = JSON.parse(atob(filters))

    return params
  }, [query])

  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)

  const [search, setSearch] = useState(queryParams.search)
  const [filters, setFilters] = useState<QueryFilter[]>(queryParams.filters)

  const [sort, setSort] = useState<Record<string, number>>({ ordered_at: -1 })
  const sortableValues = ['client.account_name', 'ordered_at', 'activated_at']
  const [sortedValue, setSortedValue] = useState<string>('ordered_at')
  const [isSortedDesc, setIsSortedDesc] = useState(false)

  const [total, setTotal] = useState(0)
  const [order, setOrder] = useState<ReadResult<Order>>()
  const [orders, setOrders] = useState<ReadResult<Order>[]>([])

  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const maxItemsPerPage = config.items_per_list_page

  const buttonText = useMemo(() => {
    const orderedAtFilters = filters.filter((f) => f.key === 'ordered_at')

    if (orderedAtFilters.length > 0) {
      const startDate = new Date(
        `${orderedAtFilters.find((f) => f.op === 'gte')?.value}T00:00:00`
      )
      const endDate = new Date(
        `${orderedAtFilters.find((f) => f.op === 'lte')?.value}T00:00:00`
      )

      if (startDate && endDate) {
        return `${formatDate(startDate)} - ${formatDate(endDate)}`
      }
    }

    return 'dd/mm/aaaa - dd/mm/aaaa'
  }, [filters])

  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true)

      const options = {
        attributes: [
          'id',
          'token',
          'batch',
          'quantity',
          'amount',
          'ordered_at',
          'created_at',
          'current_status',
          'activated_at'
        ],
        includes: {
          client: ['id', 'account_name', 'document', 'document_type'],
          plan: ['id', 'name', 'nature', 'amount', 'currency'],
          product: ['*'],
          staff_member: ['id', 'fullname']
        },
        limit: maxItemsPerPage,
        filters,
        sort,
        search,
        page
      }

      try {
        const { count, data } = await list(options)
        setOrders(data)
        setTotal(count)
        setPage(page)
        setIsLoading(false)
      } catch (err) {
        setIsLoading(false)
        toast.error('Falha ao buscar pedidos')
      }
    }

    fetchOrders()
  }, [page, search, sort, filters, maxItemsPerPage])

  useEffect(() => {
    const newLastPage = Math.ceil(total / maxItemsPerPage)
    setLastPage(newLastPage)
  }, [maxItemsPerPage, total])

  const onSort = useCallback(
    (sortBy: string) => {
      if (!sortableValues.includes(sortBy)) return
      const sortValue = isSortedDesc ? -1 : 1
      setSort({ [sortBy]: sortValue })
      setSortedValue(sortBy)
      setIsSortedDesc(!isSortedDesc)
    },
    [isSortedDesc]
  )

  const removeDateFilters = useCallback(() => {
    setFilters(filters.filter((f) => f.key !== 'ordered_at'))
  }, [])

  const onPageChange = useCallback(
    (page: number) => {
      const newPage = Math.min(Math.max(1, page), lastPage)
      setPage(newPage)
    },
    [lastPage]
  )

  const onFilter = useCallback(
    (key: string, value: any) => {
      const cleanFilters = removeFilter(filters, key)

      if (value === '') {
        if (cleanFilters.length > 0) {
          query.set('q', btoa(JSON.stringify(cleanFilters)))
        } else {
          query.delete('q')
        }

        navigate(
          {
            pathname: window.location.pathname,
            search: query.toString()
          },
          { replace: true }
        )

        setFilters([...cleanFilters])
        return
      }

      if (key === 'current_status') {
        cleanFilters.push({ key, op: 'eq', value })
      }

      if (key === 'ordered_at') {
        cleanFilters.push(
          { key, op: 'gte', value: value[0] },
          { key, op: 'lte', value: value[1] }
        )
      }

      if (cleanFilters.length > 0) {
        query.set('q', btoa(JSON.stringify(cleanFilters)))
      } else {
        query.delete('q')
      }

      setFilters([...cleanFilters])

      navigate(
        {
          pathname: window.location.pathname,
          search: query.toString()
        },
        { replace: true }
      )
    },
    [filters, navigate, query]
  )

  const onSearch = useCallback(
    (term: string) => {
      if (!term || !term.length || term.length < config.min_search_length) {
        term = ''
      }

      if (term === '') {
        query.delete('search')
      } else {
        query.set('search', term)
      }

      navigate(
        {
          pathname: window.location.pathname,
          search: query.toString()
        },
        { replace: true }
      )

      setSearch(term)
    },
    [query, navigate]
  )

  const onShowModal = useCallback(
    (id: number) => {
      setOrder(orders[id])
      setIsOpen(true)
    },
    [orders]
  )

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

  const toggleDatePicker = () => {
    setIsDatePickerOpen(!isDatePickerOpen)
  }

  return (
    <>
      <PageHeader title="Pedidos" action="Listagem">
        <SearchBar value={search} onChange={onSearch} />
        <ProtectedSection roles={[Role.MANAGER, Role.SALES]}>
          <Link to="/orders/create">
            <SimpleButton>Novo</SimpleButton>
          </Link>
        </ProtectedSection>
      </PageHeader>

      <div className="w-full px-4 my-4 h-16 flex items-center justify-between overflow-visible">
        <div className="w-1/5">
          <SimpleCustomSelect
            label=""
            options={statusFilters}
            value={
              filters?.find((filter) => filter.key === 'current_status')
                ?.value ?? statusFilters[0].value
            }
            onChange={(value) => onFilter('current_status', value)}
          />
        </div>

        <div className="flex overflow-visible relative">
          <div className="w-full">
            <button
              onClick={toggleDatePicker}
              className="px-4 py-2 border rounded-lg bg-white text-black font-medium text-sm"
            >
              {buttonText}
            </button>

            {isDatePickerOpen && (
              <div className="absolute top-full right-0.5 w-[335px] max-h-[430px] overflow-y-auto z-50">
                <DateRangePickerComponent
                  filters={filters}
                  onFilter={onFilter}
                  onClose={toggleDatePicker}
                  cleanRange={removeDateFilters}
                  columnName="ordered_at"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <LoadingCircle />
      ) : (
        <>
          <div className="flex flex-col animate-fade-in-down">
            <div className="-my-2 overflow-x-auto px-4">
              <div className="py-2 align-middle inline-block min-w-full">
                <div className="shadow border-b border-gray-200 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-light text-gray-500"
                        >
                          CÓDIGO
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-light text-gray-500"
                        >
                          ESTADO
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-light text-gray-500 group cursor-pointer"
                          onClick={() => onSort('client.account_name')}
                        >
                          <div className={'flex justify-between items-center'}>
                            <div>CONTA</div>
                            {sortedValue === 'client.account_name' ? (
                              isSortedDesc ? (
                                <SortDownIcon className="h-4 w-4" />
                              ) : (
                                <SorUptIcon className="h-4 w-4" />
                              )
                            ) : (
                              <SortIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />
                            )}
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-light text-gray-500"
                        >
                          PLANO
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-light text-gray-500"
                        >
                          VALOR UNITÁRIO
                        </th>
                        <th
                          scope="col"
                          className="group cursor-pointer px-4 py-3 text-left text-sm font-light text-gray-500"
                          onClick={() => onSort('ordered_at')}
                        >
                          <div className={'flex justify-between items-center'}>
                            <div>PEDIDO EM</div>
                            {sortedValue === 'ordered_at' ? (
                              isSortedDesc ? (
                                <SortDownIcon className="h-4 w-4" />
                              ) : (
                                <SorUptIcon className="h-4 w-4" />
                              )
                            ) : (
                              <SortIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />
                            )}
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="group cursor-pointer px-4 py-3 text-left text-sm font-light text-gray-500"
                          onClick={() => onSort('activated_at')}
                        >
                          <div className={'flex justify-between items-center'}>
                            <div>ATIVADO EM</div>
                            {sortedValue === 'activated_at' ? (
                              isSortedDesc ? (
                                <SortDownIcon className="h-4 w-4" />
                              ) : (
                                <SorUptIcon className="h-4 w-4" />
                              )
                            ) : (
                              <SortIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />
                            )}
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-light text-gray-500"
                        >
                          RESPONSÁVEL
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-sm font-light text-gray-500"
                        >
                          AÇÕES
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orders.map((order, index) => (
                        <tr
                          key={order.id}
                          className={
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }
                        >
                          <td className="px-4 py-3 text-left text-sm whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-1 truncate">
                                <Link
                                  to={`/orders/${order.id}/${
                                    order.relationships?.product?.attributes
                                      ?.type === PlanType.connectivity
                                      ? 'chips'
                                      : 'devices'
                                  }`}
                                  className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 hover:underline truncate"
                                >
                                  {order.attributes.token}
                                </Link>
                                <div className="text-xs text-left text-gray-500">
                                  {' '}
                                  {order.attributes.batch}{' '}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-left text-sm whitespace-nowrap">
                            <div className="text-sm text-left font-semibold text-gray-900 truncate">
                              <Label
                                type="order_status"
                                label={
                                  translations['order_status'][
                                    order.attributes.current_status!
                                  ]
                                }
                                value={order.attributes.current_status}
                              />
                            </div>
                            <div className="ml-2 text-xs text-left text-gray-500">
                              {' '}
                              {date(
                                getOrderStatusAndDate(order).date as Date
                              )}{' '}
                            </div>
                          </td>
                          <td className="px-4 py-3 max-w-xxs whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-1 truncate">
                                <div
                                  title={
                                    order.relationships?.client.attributes
                                      .account_name
                                  }
                                  className="text-xs text-left font-semibold text-gray-900 truncate"
                                >
                                  {
                                    order.relationships?.client.attributes
                                      .account_name
                                  }
                                </div>
                                <div className="text-xs text-left text-gray-500">
                                  {document(
                                    order.relationships?.client.attributes
                                      .document,
                                    order.relationships?.client.attributes
                                      .document_type
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap max-w-xs text-sm text-gray-500">
                            <div className="flex items-center">
                              <div className="flex-1 truncate">
                                <div className="text-xs text-left font-semibold text-gray-900 truncate">
                                  {' '}
                                  {order.relationships?.plan.attributes.name ??
                                    '-'}{' '}
                                </div>
                                <div className="text-xs text-left text-gray-500">
                                  {' '}
                                  {
                                    translations.plan_nature[
                                      order.relationships?.plan.attributes
                                        .nature
                                    ]
                                  }{' '}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex">
                              <div className="flex-1">
                                <div className="text-xs text-left font-semibold text-gray-900">
                                  {' '}
                                  {`${currency(
                                    order.relationships?.plan.attributes
                                      .currency
                                  )} ${amount(order.attributes.amount)}`}{' '}
                                </div>
                                <div className="text-xs text-left text-gray-500">
                                  {' '}
                                  {`Quantidade: ${order.attributes.quantity}`}{' '}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-left text-sm whitespace-nowrap">
                            <div className="flex">
                              <div className="flex-1">
                                <div className="text-xs text-left font-semibold text-gray-900">
                                  {date(order.attributes.ordered_at)}
                                </div>
                                <div className="text-xs text-left text-gray-500">
                                  {time(order.attributes.ordered_at)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-left text-sm whitespace-nowrap">
                            <div className="flex">
                              <div className="flex-1">
                                <div className="text-xs text-left font-semibold text-gray-900">
                                  {date(order.attributes.activated_at)}
                                </div>
                                <div className="text-xs text-left text-gray-500">
                                  {time(order.attributes.activated_at)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 max-w-xxs whitespace-nowrap">
                            <div className="text-xs text-left text-gray-900 truncate">
                              {order.relationships?.staff_member?.attributes
                                ?.fullname ?? '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-left text-gray-500">
                            <RowActionsDropdown
                              actions={[
                                {
                                  label: 'Editar',
                                  icon: PencilIcon,
                                  href: `/orders/${order.id}`,
                                  show:
                                    order.attributes.current_status ===
                                    'pending'
                                },
                                {
                                  label: 'Dividir Pedido',
                                  icon: DuplicateIcon,
                                  onClick: () => onShowModal(index),
                                  show: ['approved', 'in_line'].includes(
                                    order.attributes.current_status!
                                  )
                                }
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="mx-4">
              <Pagination
                currentPage={page}
                pageSize={maxItemsPerPage}
                itemsOnPage={orders.length}
                totalItems={total}
                onPageChange={onPageChange}
              />
            </div>
          </div>
        </>
      )}

      <SplitModal order={order} open={isOpen} setOpen={setIsOpen} />
    </>
  )
}
